import type React from "react"
import { createContext, useContext, useState, useCallback } from "react"

export interface CartItem {
    id: number
    name: string
    price: number
    image: string
    quantity: number
}

interface CartContextType {
    items: CartItem[]
    addItem: (item: CartItem) => void
    removeItem: (id: number) => void
    updateQuantity: (id: number, quantity: number) => void
    clearCart: () => void
    total: number
}

const CartContext = createContext<CartContextType | undefined>(undefined)

export function CartProvider({ children }: { children: React.ReactNode }) {
    const [items, setItems] = useState<CartItem[]>([])

    const addItem = useCallback((item: CartItem) => {
        setItems((prevItems) => {
            const existingItem = prevItems.find((i) => i.id === item.id)
            if (existingItem) {
                return prevItems.map((i) => (i.id === item.id ? { ...i, quantity: i.quantity + item.quantity } : i))
            }
            return [...prevItems, item]
        })
    }, [])

    const removeItem = useCallback((id: number) => {
        setItems((prevItems) => prevItems.filter((i) => i.id !== id))
    }, [])

    const updateQuantity = useCallback((id: number, quantity: number) => {
        setItems((prevItems) => prevItems.map((i) => (i.id === id ? { ...i, quantity: Math.max(0, quantity) } : i)))
    }, [])

    const clearCart = useCallback(() => {
        setItems([])
    }, [])

    const total = items.reduce((sum, item) => sum + item.price * item.quantity, 0)

    return (
        <CartContext.Provider value={{ items, addItem, removeItem, updateQuantity, clearCart, total }}>
            {children}
        </CartContext.Provider>
    )
}

export function useCart() {
    const context = useContext(CartContext)
    if (!context) {
        throw new Error("useCart must be used within CartProvider")
    }
    return context
}
